class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async refresh(dontRefreshCartItems,items_count) {
    try {
      const sectionIds = this.getSectionsToRender().map(section => section.id);
      const cartResponse = await fetch(`?sections=${sectionIds.join(',')}`);
      const sections = await cartResponse.json();
  
      const ADDTOCART_OBJECT = { sections };
      this.renderContents(ADDTOCART_OBJECT);
  
      if (this.classList.contains('is-empty')) {
        this.classList.remove('is-empty');
      }
      if(items_count<= 0){
        if (!this.classList.contains('is-empty')) this.classList.add('is-empty');
      }
    } catch (error) {
      console.log(error); // eslint-disable-line
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: this.errorMsg?.textContent || 'Unknown cart error'
        }
      }));
    }
  }

}

//upsell gift product add to cart using component
class GiftProduct extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('#giftcart');
    this.variantId = +this.getAttribute('pdp-id');
    this.qtyInput = this.querySelector('.quantity__input');
    this.plusBtn = this.querySelector('.qty-plus');
    this.minusBtn = this.querySelector('.qty-minus');

    if (this.qtyInput) this.qtyInput.value ||= 1;

    this.plusBtn?.addEventListener('click', () => this.qtyInput.value = +this.qtyInput.value + 1);
    this.minusBtn?.addEventListener('click', () => {
      if (+this.qtyInput.value > 1) this.qtyInput.value = +this.qtyInput.value - 1;
    });
    this.checkbox?.addEventListener('change', () => this.toggleGift());

    this.syncCheckbox();
  }

  async syncCheckbox() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      this.checkbox.checked = cart.items.some(i => i.variant_id === this.variantId);
    } catch {
      /* fail silently */
    }
  }

  async toggleGift() {
    const qty = +this.qtyInput.value || 1;
    const cartDrawer = document.querySelector('cart-drawer');

    if (this.checkbox.checked) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantId, quantity: qty }),
      });
      await cartDrawer?.refresh?.(true);
      this.checkbox.checked = true;
    } else {
      const cart = await (await fetch('/cart.js')).json();
      const item = cart.items.find(i => i.variant_id === this.variantId);
      if (!item) return;

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });

      if (res.ok) {
        await cartDrawer?.refresh?.(true);
        this.checkbox.checked = false;
      }
    }
  }
}

customElements.define('gift-product', GiftProduct);

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
